import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Check, X, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';

type BillingPeriod = 'monthly' | 'annual';

export function PricingSection() {
  const navigate = useNavigate();
  const [billingPeriod, setBillingPeriod] = useState<BillingPeriod>('monthly');

  const { data: plans, isLoading } = useQuery({
    queryKey: ['public_plans'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('subscription_plans')
        .select('*')
        .eq('is_active', true)
        .order('display_order', { ascending: true });

      if (error) throw error;
      return data;
    },
  });

  const getPrice = (plan: any) => {
    return billingPeriod === 'monthly' ? plan.price_monthly : plan.price_annual;
  };

  const getPriceLabel = () => {
    return billingPeriod === 'monthly' ? '/mês' : '/ano';
  };

  const handleSelectPlan = (planId: string) => {
    navigate(`/auth?signup=true&plan=${planId}`);
  };

  const getIntegrationsLabel = (plan: any) => {
    if (plan.integrations_mode === 'customizable') {
      return `Até ${plan.max_integrations} integrações customizáveis`;
    } else {
      const count = (plan.allowed_integrations as string[])?.length || 0;
      return count === 0 ? 'Integrações ilimitadas' : `${count} integração(ões)`;
    }
  };

  const getBasicMetricsLabel = (plan: any) => {
    const count = (plan.basic_metrics as string[])?.length || 0;
    return count > 0 ? `${count} Métricas básicas` : 'Métricas básicas incluídas';
  };

  const getAdvancedMetricsLabel = (plan: any) => {
    if (!plan.advanced_metrics_enabled) return 'Métricas avançadas';
    
    if (plan.advanced_metrics_mode === 'customizable') {
      return `Métricas avançadas customizáveis (até ${plan.max_advanced_metrics})`;
    } else {
      const count = (plan.advanced_metrics as string[])?.length || 0;
      return count > 0 ? `${count} Métricas avançadas` : 'Métricas avançadas';
    }
  };

  const getAllFeatures = (plan: any) => {
    return [
      {
        included: true,
        label: plan.max_users === 0 ? 'Usuários ilimitados' : `Até ${plan.max_users} usuário(s)`,
      },
      {
        included: true,
        label: (plan as any).max_products === 0 ? 'Produtos ilimitados' : `Até ${(plan as any).max_products} produto(s)`,
      },
      {
        included: true,
        label: getIntegrationsLabel(plan),
      },
      {
        included: plan.basic_metrics && (plan.basic_metrics as string[]).length > 0,
        label: getBasicMetricsLabel(plan),
      },
      {
        included: plan.advanced_metrics_enabled,
        label: getAdvancedMetricsLabel(plan),
      },
      {
        included: plan.goals_enabled,
        label: plan.goals_enabled 
          ? (plan.max_goals === 0 ? 'Metas ilimitadas' : `Até ${plan.max_goals} meta(s)`)
          : 'Metas',
      },
      {
        included: plan.manual_entry_enabled,
        label: 'Lançamento manual',
      },
      {
        included: plan.bulk_import_enabled,
        label: 'Importação em massa',
      },
      {
        included: plan.priority_support,
        label: 'Suporte prioritário',
      },
    ];
  };

  if (isLoading) {
    return (
      <section id="pricing" className="py-20 md:py-32 bg-muted/50">
        <div className="container mx-auto px-4 text-center">
          <p className="text-muted-foreground">Carregando planos...</p>
        </div>
      </section>
    );
  }

  return (
    <section id="pricing" className="py-20 md:py-32 bg-muted/50">
      <div className="container mx-auto px-4">
        <div className="mx-auto max-w-2xl text-center space-y-4 mb-12">
          <h2 className="text-3xl md:text-4xl font-bold">
            Escolha o plano ideal para você
          </h2>
          <p className="text-xl text-muted-foreground">
            Comece grátis e escale conforme seu negócio cresce
          </p>
        </div>

        {/* Billing Period Toggle */}
        <div className="flex justify-center mb-12">
          <Tabs value={billingPeriod} onValueChange={(value) => setBillingPeriod(value as BillingPeriod)}>
            <TabsList className="grid w-full grid-cols-2 max-w-md">
              <TabsTrigger value="monthly">Mensal</TabsTrigger>
              <TabsTrigger value="annual">
                Anual
                <Badge variant="secondary" className="ml-2">-20%</Badge>
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
          {plans?.map((plan, index) => {
            const price = getPrice(plan);
            const isPopular = index === 1; // Middle plan as popular
            
            const features = getAllFeatures(plan);
            
            return (
              <Card 
                key={plan.id} 
                className={`relative flex flex-col ${isPopular ? 'border-primary shadow-xl scale-105' : 'border-2'}`}
              >
                {isPopular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <Badge className="gap-1">
                      <Sparkles className="h-3 w-3" />
                      Mais Popular
                    </Badge>
                  </div>
                )}

                <CardHeader className="space-y-4">
                  <CardTitle className="text-2xl">{plan.name}</CardTitle>
                  <div className="space-y-2">
                    <div className="flex items-baseline gap-1">
                      <span className="text-4xl font-bold">
                        R$ {price?.toFixed(2)}
                      </span>
                      <span className="text-muted-foreground">
                        {getPriceLabel()}
                      </span>
                    </div>
                    {billingPeriod === 'annual' && plan.price_monthly > 0 && (
                      <p className="text-sm text-muted-foreground">
                        Economize R$ {((plan.price_monthly * 12) - plan.price_annual).toFixed(2)}/ano
                      </p>
                    )}
                  </div>
                </CardHeader>

                <CardContent className="flex-1 space-y-4">
                  <div className="space-y-3">
                    {features.map((feature, idx) => (
                      <div key={idx} className="flex items-start gap-2">
                        {feature.included ? (
                          <Check className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                        ) : (
                          <X className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" />
                        )}
                        <span className={feature.included ? '' : 'text-muted-foreground'}>
                          {feature.label}
                        </span>
                      </div>
                    ))}
                  </div>
                </CardContent>

                <CardFooter className="mt-auto">
                  <Button
                    className="w-full"
                    variant={isPopular ? 'default' : 'outline'}
                    onClick={() => handleSelectPlan(plan.id)}
                  >
                    Começar Agora
                  </Button>
                </CardFooter>
              </Card>
            );
          })}
        </div>

        {/* View Details Button */}
        <div className="flex justify-center mt-12">
          <Button
            variant="outline"
            size="lg"
            onClick={() => navigate('/pricing-details')}
            className="gap-2"
          >
            Ver comparação detalhada dos planos
          </Button>
        </div>
      </div>
    </section>
  );
}
